import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Button, Card, Flex, Input, Modal, Tag, Tooltip, Typography, message as antMessage } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotate, faTrash, faCopy, faKey } from '@fortawesome/pro-light-svg-icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import {
	GET_MCP_ACCESS,
	GENERATE_MCP_TOKEN,
	REVOKE_MCP_TOKEN,
	REGENERATE_MCP_TOKEN
} from 'graph/documents';

const { Text, Paragraph } = Typography;

interface McpTokenInfo {
	prefix: string;
	name?: string | null;
	createdAt: string;
	lastUsedAt?: string | null;
	revokedAt?: string | null;
}

interface McpAccess {
	endpoint?: string | null;
	token?: string | null;
	info?: McpTokenInfo | null;
}

const useCopy = () => {
	const { t } = useTranslation();
	return async (value: string) => {
		try {
			await navigator.clipboard.writeText(value);
			antMessage.success(t('pages.profile.api_access.copied'));
		} catch {
			antMessage.error(t('pages.profile.api_access.copy_failed'));
		}
	};
};

export const ApiAccessSection: React.FC = () => {
	const { t } = useTranslation();
	const copy = useCopy();
	const { data, loading, error: queryError, refetch } = useQuery<{ getMcpAccess: McpAccess }>(GET_MCP_ACCESS, {
		fetchPolicy: 'cache-and-network'
	});

	const [generate, { loading: generating }] = useMutation<{ generateMcpToken: McpAccess }>(GENERATE_MCP_TOKEN);
	const [revoke, { loading: revoking }] = useMutation<{ revokeMcpToken: McpAccess }>(REVOKE_MCP_TOKEN);
	const [regenerate, { loading: regenerating }] = useMutation<{ regenerateMcpToken: McpAccess }>(REGENERATE_MCP_TOKEN);

	const [revealToken, setRevealToken] = useState<string | null>(null);
	const [actionError, setActionError] = useState<string | null>(null);

	const access = data?.getMcpAccess;
	const info = access?.info;
	const endpoint = access?.endpoint || '';

	const reportError = (label: string, e: unknown) => {
		const msg = e instanceof Error ? e.message : String(e);
		console.error(`[MCP] ${label} failed:`, e);
		setActionError(msg);
		antMessage.error(msg);
	};

	const triggerGenerate = async () => {
		setActionError(null);
		try {
			const res = await generate({ variables: { name: t('pages.profile.api_access.default_token_name') } });
			const tok = res.data?.generateMcpToken?.token;
			if (tok) setRevealToken(tok);
			await refetch();
		} catch (e) {
			reportError('generateMcpToken', e);
		}
	};

	const onRegenerate = () => {
		Modal.confirm({
			title: t('pages.profile.api_access.regenerate_confirm_title'),
			content: t('pages.profile.api_access.regenerate_confirm_body'),
			okText: t('pages.profile.api_access.regenerate'),
			okButtonProps: { danger: true },
			onOk: async () => {
				setActionError(null);
				try {
					const res = await regenerate({ variables: { name: info?.name } });
					const tok = res.data?.regenerateMcpToken?.token;
					if (tok) setRevealToken(tok);
					await refetch();
				} catch (e) {
					reportError('regenerateMcpToken', e);
				}
			}
		});
	};

	const onRevoke = () => {
		Modal.confirm({
			title: t('pages.profile.api_access.revoke_confirm_title'),
			content: t('pages.profile.api_access.revoke_confirm_body'),
			okText: t('pages.profile.api_access.revoke'),
			okButtonProps: { danger: true },
			onOk: async () => {
				setActionError(null);
				try {
					await revoke();
					await refetch();
				} catch (e) {
					reportError('revokeMcpToken', e);
				}
			}
		});
	};

	const claudeConfigSnippet = useMemo(() => {
		if (!endpoint) return '';
		return JSON.stringify(
			{
				mcpServers: {
					gerositus: {
						url: endpoint,
						headers: { Authorization: 'Bearer <YOUR_TOKEN>' }
					}
				}
			},
			null,
			2
		);
	}, [endpoint]);

	const status = info
		? info.revokedAt
			? { color: 'red', text: t('pages.profile.api_access.status_revoked') }
			: { color: 'green', text: t('pages.profile.api_access.status_active') }
		: null;

	const subtitle = info?.lastUsedAt
		? t('pages.profile.api_access.last_used_at', {
				date: dayjs(info.lastUsedAt).format('DD/MM/YYYY HH:mm')
		  })
		: info
		? t('pages.profile.api_access.never_used')
		: null;

	return (
		<div className="ApiAccess">
			<div className="title">{t('pages.profile.api_access.title')}</div>
			<div className="subtitle">{t('pages.profile.api_access.description')}</div>

			{(queryError || actionError) && (
				<Alert
					type="error"
					showIcon
					closable={!!actionError}
					className="mt-2"
					message={queryError?.message || actionError}
					onClose={() => setActionError(null)}
				/>
			)}

			{loading && !data ? (
				<Text type="secondary">{t('please_wait')}</Text>
			) : (
				<Card size="small" className="mcp-card">
					{endpoint && (
						<Flex gap={8} align="center" className="mcp-endpoint">
							<Text type="secondary" className="mcp-endpoint-url" ellipsis={{ tooltip: endpoint }}>
								{endpoint}
							</Text>
							<Tooltip title={t('pages.profile.api_access.copy')}>
								<Button type="text" size="small" icon={<FontAwesomeIcon icon={faCopy} />} onClick={() => copy(endpoint)} />
							</Tooltip>
						</Flex>
					)}

					{info ? (
						<>
							<Flex gap={10} align="center" wrap="wrap" className="mcp-token-row">
								<FontAwesomeIcon icon={faKey} className="mcp-key-icon" />
								<Text strong className="mcp-prefix">{info.prefix}</Text>
								{status && <Tag color={status.color} bordered={false}>{status.text}</Tag>}
							</Flex>
							{subtitle && <Text type="secondary" className="mcp-meta">{subtitle}</Text>}

							<Flex gap={4} className="mcp-actions" wrap="wrap">
								{!info.revokedAt ? (
									<>
										<Button
											type="text"
											size="small"
											icon={<FontAwesomeIcon icon={faRotate} />}
											loading={regenerating}
											onClick={onRegenerate}
										>
											{t('pages.profile.api_access.regenerate')}
										</Button>
										<Button
											type="text"
											size="small"
											danger
											icon={<FontAwesomeIcon icon={faTrash} />}
											loading={revoking}
											onClick={onRevoke}
										>
											{t('pages.profile.api_access.revoke')}
										</Button>
									</>
								) : (
									<Button type="primary" size="small" loading={generating} onClick={triggerGenerate}>
										{t('pages.profile.api_access.generate')}
									</Button>
								)}
							</Flex>
						</>
					) : (
						<Flex vertical gap={8} align="flex-start" className="mcp-empty">
							<Text type="secondary">{t('pages.profile.api_access.no_token')}</Text>
							<Button type="primary" loading={generating} onClick={triggerGenerate}>
								{t('pages.profile.api_access.generate')}
							</Button>
						</Flex>
					)}
				</Card>
			)}

			{endpoint && info && !info.revokedAt && (
				<details className="mcp-config">
					<summary>{t('pages.profile.api_access.config_example')}</summary>
					<Paragraph copyable={{ text: claudeConfigSnippet }}>
						<pre>{claudeConfigSnippet}</pre>
					</Paragraph>
				</details>
			)}

			<Modal
				open={!!revealToken}
				title={t('pages.profile.api_access.reveal_title')}
				onCancel={() => setRevealToken(null)}
				footer={[
					<Button key="copy" icon={<FontAwesomeIcon icon={faCopy} />} onClick={() => revealToken && copy(revealToken)}>
						{t('pages.profile.api_access.copy')}
					</Button>,
					<Button key="ok" type="primary" onClick={() => setRevealToken(null)}>
						{t('pages.profile.api_access.reveal_done')}
					</Button>
				]}
			>
				<Alert
					type="warning"
					showIcon
					message={t('pages.profile.api_access.reveal_warning_title')}
					description={t('pages.profile.api_access.reveal_warning_body')}
					className="mb-2"
				/>
				<Input.TextArea readOnly autoSize value={revealToken || ''} style={{ fontFamily: 'monospace', fontSize: 12 }} />
			</Modal>
		</div>
	);
};
